import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Users,
  Shield,
  User,
  Menu,
  X,
  Home,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Video,
  ListCheck,
  GraduationCap,
  Film,
  Book,
  Mail,
  Building2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { API_ENDPOINTS } from "@/constants/constants";

interface AdminSidebarProps {
  onCollapsedChange?: (collapsed: boolean) => void;
}

interface Message {
  id: string;
  email: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export default function AdminSidebar({ onCollapsedChange }: AdminSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState<{ name?: string; role?: string } | null>(
    null
  );
  const [unreadMessages, setUnreadMessages] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is authenticated
    const accessToken = localStorage.getItem("accessToken");
    const userStr = localStorage.getItem("user");

    if (accessToken && userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
        fetchUnreadMessages();
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  // Rafraîchir le nombre de messages non lus lorsque la route change
  useEffect(() => {
    // Si l'utilisateur est authentifié
    if (localStorage.getItem("accessToken")) {
      fetchUnreadMessages();
    }
  }, [location.pathname]);

  // Fonction pour récupérer le nombre de messages non lus
  const fetchUnreadMessages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CONTACT.GET_MESSAGES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erreur lors de la récupération des messages: ${response.status}`
        );
      }

      const data = await response.json();

      // Calculer le nombre de messages non lus
      const notReadCount = data.filter(
        (message: Message) => !message.isRead
      ).length;
      setUnreadMessages(notReadCount);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des messages non lus:",
        error
      );
    }
  };

  // Function to handle responsive sidebar toggle
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Function to toggle sidebar collapse state
  const toggleSidebarCollapse = () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);

    // Notify parent component about the change
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsedState);
    }
  };

  const handleLogout = async () => {
    try {
      // Récupérer le token d'accès
      const accessToken = localStorage.getItem("accessToken");

      // Appeler l'API de déconnexion
      await fetch(API_ENDPOINTS.AUTH.LOGOUT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Nettoyer le localStorage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("previousPath");

      // Mettre à jour l'état local
      setUser(null);

      // Rediriger vers la page de connexion
      navigate("/login");
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      // En cas d'erreur, on redirige quand même vers la page de connexion
      navigate("/login");
    }
  };

  // Menu items for admin
  const menuItems = [
    {
      title: "Tableau de bord",
      icon: <Home size={20} />,
      path: "/admin/dashboard",
    },
    {
      title: "Messages",
      icon: <Mail size={20} />,
      path: "/admin/messages",
    },
    {
      title: "Ministères",
      icon: <Building2 size={20} />,
      path: "/admin/ministries",
    },
    {
      title: "Gestion Utilisateurs",
      icon: <Users size={20} />,
      path: "/admin/users",
    },
    {
      title: "Cinépro",
      icon: <Film size={20} />,
      path: "/admin/cinépro",
    },
    {
      title: "Ressources Pédagogiques",
      icon: <Book size={20} />,
      path: "/admin/ressources-pedagogiques",
    },
    {
      title: "Professeurs et Experts",
      icon: <Users size={20} />,
      path: "/admin/professeurs-experts",
    },
    {
      title: "Forum",
      icon: <MessageSquare size={20} />,
      path: "/admin/forum",
    },
    {
      title: "Chat",
      icon: <MessageSquare size={20} />,
      path: "/admin/chat",
    },
    {
      title: "Live/Direct",
      icon: <Video size={20} />,
      path: "/admin/live-direct",
    },
    {
      title: "Évaluation & Quizzes",
      icon: <ListCheck size={20} />,
      path: "/admin/evaluation",
    },
    {
      title: "Certificat",
      icon: <GraduationCap size={20} />,
      path: "/admin/certificat",
    },
    {
      title: "Profil",
      icon: <User size={20} />,
      path: "/admin/profile",
    },
  ];

  return (
    <>
      {/* Mobile Sidebar Toggle Button */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden bg-[#2a3f54] text-white p-2 rounded-md"
        onClick={toggleSidebar}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#2a3f54] text-white transition-all duration-300 ease-in-out z-50 
                    ${
                      sidebarOpen ? "translate-x-0" : "-translate-x-full"
                    } md:translate-x-0
                    ${sidebarCollapsed ? "w-16" : "w-64"} flex flex-col`}
      >
        {/* Logo & Brand */}
        <div
          className={`p-4 border-b border-[#374a5e] flex ${
            sidebarCollapsed ? "justify-center" : "justify-between"
          } items-center h-16 flex-shrink-0`}
        >
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center">
                <span className="text-2xl font-bold text-orange-500">
                  Guide
                </span>
                <span className="text-2xl font-bold text-green-400">Pro</span>
                <span className="ml-2 text-sm font-medium">| Admin</span>
              </div>
              <button
                onClick={toggleSidebarCollapse}
                className="p-2 rounded-full bg-[#1c2a3a] hover:bg-[#15202e] text-white"
                title="Réduire"
              >
                <ChevronLeft size={18} />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <span className="text-2xl font-bold text-orange-500">G</span>
                <span className="text-2xl font-bold text-green-400">P</span>
              </div>
              <button
                onClick={toggleSidebarCollapse}
                className="p-2 rounded-full bg-[#1c2a3a] hover:bg-[#15202e] text-white ml-1"
                title="Développer"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        {/* Admin Profile */}
        <div
          className={`p-4 border-b border-[#374a5e] ${
            sidebarCollapsed ? "flex justify-center" : ""
          } flex-shrink-0`}
        >
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src="/images/avatar-placeholder.png"
                    alt="Photo de profil"
                  />
                  <AvatarFallback className="bg-blue-700 text-white">
                    {user?.name ? (
                      user.name.substring(0, 2).toUpperCase()
                    ) : (
                      <Shield size={16} />
                    )}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {user?.name || "Administrateur"}
                  </p>
                  <p className="text-xs text-gray-400">Admin</p>
                </div>
              </div>
            </>
          ) : (
            <Avatar className="h-10 w-10">
              <AvatarImage
                src="/images/avatar-placeholder.png"
                alt="Photo de profil"
              />
              <AvatarFallback className="bg-blue-700 text-white">
                {user?.name ? (
                  user.name.substring(0, 2).toUpperCase()
                ) : (
                  <Shield size={16} />
                )}
              </AvatarFallback>
            </Avatar>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="mt-4 flex-1 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center px-6 py-3 text-sm ${
                    location.pathname === item.path
                      ? "bg-[#1c2a3a] border-l-4 border-green-400"
                      : "hover:bg-[#374a5e]"
                  } ${sidebarCollapsed ? "justify-center px-2" : ""}`}
                  title={sidebarCollapsed ? item.title : ""}
                >
                  <span className={sidebarCollapsed ? "" : "mr-3"}>
                    {item.icon}
                  </span>
                  {!sidebarCollapsed && (
                    <span className="flex items-center">
                      {item.title}
                      {item.title === "Messages" && unreadMessages > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs font-medium rounded-full px-2 py-0.5">
                          {unreadMessages}
                        </span>
                      )}
                    </span>
                  )}
                  {sidebarCollapsed &&
                    item.title === "Messages" &&
                    unreadMessages > 0 && (
                      <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-medium rounded-full px-1.5 py-0.5 translate-x-1/2 -translate-y-1/2">
                        {unreadMessages}
                      </span>
                    )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="border-t border-[#374a5e] p-4 flex-shrink-0">
          <button
            onClick={handleLogout}
            className={`flex items-center text-red-400 hover:text-red-300 w-full ${
              sidebarCollapsed ? "justify-center" : "space-x-2"
            }`}
            title={sidebarCollapsed ? "Déconnexion" : ""}
          >
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
